// useTopLevelNodeCreate.js

import { useCallback, useRef, useState } from 'react';

import { toast } from 'react-toastify';

// Utilities
import { SectionType } from 'utilities/Enums';
import { buildCollectionPath } from 'utilities/collectionPaths';
import { createNode as createNodeFromType } from 'utilities/NodeFactory';
import { getPluralLabel, getSingularLabel } from 'utilities/Languages';
import { buildFileMetadata } from 'editor/nodeadd/utilities/NodeAddUtilities';
import { detectFileNodeType, getResolvedFields } from 'utilities/NodeType';
import { resolveCreateNodeModule } from 'utilities/createNodeModuleContext';
import { normalizeNodeModule, NODE_MODULE } from 'utilities/nodeModule';

// Services
import { upsertEdge } from 'services/nodes/NodeEdgeService';
import { createNode as createNodeDoc } from 'services/nodes/NodeWriteService';
import { uploadService } from 'services/nodes/NodeUploadService';
import { assertBaseUploadAllowed } from 'services/bases/BaseUploadPolicyService';
import { notifyBaseLimitError } from 'utilities/baseLimitFeedback';
import { registerProvisionalSession } from 'node/provisional/provisionalNodeSession';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';

const DEFAULT_UPLOAD_ACCEPT = {
    PHOTO: 'image/*',
    VIDEO: 'video/*',
    DOCUMENT: 'application/pdf,application/x-google-chrome-pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,application/msword,text/plain,.txt,application/rtf,.rtf,application/vnd.oasis.opendocument.text,.odt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.xlsx,.xls,.csv,text/csv,text/tab-separated-values,.tsv,application/vnd.oasis.opendocument.spreadsheet,.ods',
    FILE: 'image/*,video/*,application/*,text/*,.pdf',
};

/** Union accept used by the mixed Attach picker (MIME → PHOTO/VIDEO/DOCUMENT/FILE). */
export const MIXED_UPLOAD_ACCEPT = [
    DEFAULT_UPLOAD_ACCEPT.PHOTO,
    DEFAULT_UPLOAD_ACCEPT.VIDEO,
    DEFAULT_UPLOAD_ACCEPT.DOCUMENT,
    DEFAULT_UPLOAD_ACCEPT.FILE,
].join(',');

const MIXED_UPLOAD_TYPE_KEY = 'ATTACH';

const ALWAYS_MULTI_UPLOAD_PRIMITIVES = new Set(['PHOTO', 'VIDEO', 'DOCUMENT', 'FILE']);

const buildDefaultDatabaseKey = (baseKey, typeKey) => {
    if (!baseKey || !typeKey) return '';
    return `${baseKey}_${typeKey}_all`.replace(/\//g, '_');
};

function applyUploadPatchesToNodes(createdNodes, patches) {
    if (!Array.isArray(createdNodes) || !Array.isArray(patches) || !patches.length) return;

    const nodesByKey = new Map(
        createdNodes
            .filter((row) => row?.key)
            .map((row) => [row.key, row]),
    );

    patches.forEach((entry) => {
        const nodeKey = entry?.nodeKey;
        const patch = entry?.patch;
        if (!nodeKey || !patch || typeof patch !== 'object') return;
        const node = nodesByKey.get(nodeKey);
        if (!node) return;
        Object.assign(node, patch);
    });
}

export default function useTopLevelNodeCreate({
    user,
    activeBaseKey,
    getType,
    language,
    navigate,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
    setActiveDatabase,
    shouldNavigate = true,
    onNodesCreated,
    onNodesPrepared,
}) {
    const { openNode } = useNodeOpenIntent();
    const uploadInputRef = useRef(null);
    const [pendingUploadType, setPendingUploadType] = useState(null);
    const [pendingUploadCreateType, setPendingUploadCreateType] = useState(null);
    const [pendingUploadAccept, setPendingUploadAccept] = useState('');
    const [pendingUploadFields, setPendingUploadFields] = useState(null);
    const [pendingUploadNavigate, setPendingUploadNavigate] = useState(null);
    const [pendingUploadMultiple, setPendingUploadMultiple] = useState(false);
    const [pendingUploadDetectPerFile, setPendingUploadDetectPerFile] = useState(false);
    const [pendingUploadModule, setPendingUploadModule] = useState(NODE_MODULE.PRIVATE);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraContentType, setCameraContentType] = useState(null);
    const [cameraInitialFields, setCameraInitialFields] = useState(null);
    const [cameraShouldNavigate, setCameraShouldNavigate] = useState(null);
    const [cameraModule, setCameraModule] = useState(NODE_MODULE.PRIVATE);

    const notifyNodeCallback = useCallback((callback, createdNodes, label) => {
        if (typeof callback !== 'function') return;

        const rows = (Array.isArray(createdNodes) ? createdNodes : [])
            .filter((row) => row?.key);

        if (!rows.length) return;

        try {
            callback(rows);
        } catch (error) {
            console.warn(`${label} callback failed:`, error);
        }
    }, []);

    const notifyNodesPrepared = useCallback((createdNodes) => {
        notifyNodeCallback(onNodesPrepared, createdNodes, 'onNodesPrepared');
    }, [notifyNodeCallback, onNodesPrepared]);

    const notifyNodesCreated = useCallback((createdNodes) => {
        notifyNodeCallback(onNodesCreated, createdNodes, 'onNodesCreated');
    }, [notifyNodeCallback, onNodesCreated]);

    const assertFilesAllowedForBase = useCallback(async (files) => {
        const safeFiles = Array.isArray(files) ? files.filter(Boolean) : [];
        if (!safeFiles.length) return;

        await Promise.all(safeFiles.map((file) => {
            return assertBaseUploadAllowed({
                baseKey: activeBaseKey,
                fileName: file?.name,
                fileSizeBytes: file?.size,
            });
        }));
    }, [activeBaseKey]);

    const resolveSectionKeyForField = useCallback((typeKey, fieldKey) => {
        const def = getType?.(typeKey);
        const fieldName = typeof fieldKey === 'string' ? fieldKey.trim() : '';

        if (!def || !fieldName) return def?.dataSection || null;

        const schemaFields = Array.isArray(def?.schema?.fields) ? def.schema.fields : [];
        const resolvedFields = getResolvedFields(typeKey, { includeHidden: true });
        const allFields = [
            ...schemaFields,
            ...(Array.isArray(resolvedFields) ? resolvedFields : []),
        ];

        const fieldDef = allFields.find((field) => {
            const candidateName = field?.name || field?.key;
            return candidateName === fieldName;
        }) || null;

        const sourceTypeKey = String(fieldDef?.source || def?.key || typeKey || '').trim();
        if (!sourceTypeKey) return def?.dataSection || null;

        const sourceDef = getType?.(sourceTypeKey);
        if (sourceDef?.dataSection) return sourceDef.dataSection;

        const normalizedSource = sourceTypeKey.toLowerCase();
        return normalizedSource ? `data_${normalizedSource}` : (def?.dataSection || null);
    }, [getType]);

    const applyInitialFields = useCallback((targetNode, typeKey, initialFields) => {
        if (!targetNode || !initialFields) return;
        const def = getType?.(typeKey);
        if (!def) return;

        const containersBySection = new Map();

        Object.entries(initialFields).forEach(([fieldKey, value]) => {
            if (value === undefined) return;

            const sectionKey = resolveSectionKeyForField(typeKey, fieldKey);
            if (!sectionKey) return;

            const existing = containersBySection.get(sectionKey)
                || ((targetNode[sectionKey] && typeof targetNode[sectionKey] === 'object')
                    ? { ...targetNode[sectionKey] }
                    : {});

            existing[fieldKey] = value;
            containersBySection.set(sectionKey, existing);
        });

        containersBySection.forEach((container, sectionKey) => {
            if (Object.keys(container).length > 0) {
                targetNode[sectionKey] = container;
            }
        });
    }, [getType, resolveSectionKeyForField]);

    const createNodeForFile = useCallback(async ({
        file,
        createTypeKey,
        initialFields,
        module: moduleOption = null,
    }) => {
        const nodeModule = normalizeNodeModule(moduleOption) || resolveCreateNodeModule();
        const newNode = await createNodeFromType(
            createTypeKey,
            user,
            activeBaseKey || null,
            { module: nodeModule },
        );

        const typeDef = getType?.(newNode.type || createTypeKey);
        const singularLabel = getSingularLabel(typeDef, newNode.type || createTypeKey, language);
        let defaultTitle = `New ${singularLabel}`;

        newNode.parentTitle = user.name || 'Profile';
        newNode.parentType = user.type || 'USER';
        newNode.profileRoot = true;

        newNode.fileName = file.name;
        const objectUrl = URL.createObjectURL(file);
        newNode.localUri = objectUrl;
        newNode.url = objectUrl;

        const def = getType?.(createTypeKey);
        const resolvedTypeKey = def?.key || createTypeKey;
        if (resolvedTypeKey === 'DOCUMENT' || resolvedTypeKey === 'FILE') {
            const metadata = buildFileMetadata(file, resolvedTypeKey);
            if (metadata.data_document) newNode.data_document = metadata.data_document;
            if (metadata.data_file) newNode.data_file = metadata.data_file;
            if (metadata.title) defaultTitle = metadata.title;
        }

        newNode.title = defaultTitle;

        applyInitialFields(newNode, newNode.type || createTypeKey, initialFields);

        const createPayload = { ...newNode };
        delete createPayload.localUri;
        delete createPayload.url;
        delete createPayload.base64Data;
        await createNodeDoc(newNode.key, createPayload);

        return { node: newNode, file, objectUrl };
    }, [activeBaseKey, applyInitialFields, getType, language, user]);

    const processUploadFiles = useCallback(async ({
        files,
        typeKey,
        createTypeKey,
        initialFields,
        isMultiUpload,
        shouldNavigateNow,
        detectPerFile = false,
        module: moduleOption = null,
    }) => {
        if (!files.length || !typeKey || !user) return null;
        const nodeModule = normalizeNodeModule(moduleOption) || resolveCreateNodeModule();

        try {
            await assertFilesAllowedForBase(files);

            if (isMultiUpload) {
                if (shouldNavigateNow && !detectPerFile && createTypeKey) {
                    const databaseKey = buildDefaultDatabaseKey(activeBaseKey, createTypeKey);
                    if (databaseKey && navigate) {
                        if (typeof setActiveDatabase === 'function') {
                            const typeDef = getType?.(createTypeKey);
                            const pluralLabel = getPluralLabel(typeDef, createTypeKey, language);
                            setActiveDatabase({
                                key: databaseKey,
                                type: createTypeKey,
                                title: `All ${pluralLabel}`,
                                baseKey: activeBaseKey || null,
                                userKey: user?.key || null,
                                filters: [],
                            });
                        }
                        setSelectedLocation && setSelectedLocation(null);
                        setCurrentNode && setCurrentNode(null);
                        setCurrentSection && setCurrentSection(SectionType.NODE);
                        const moduleQuery = nodeModule === NODE_MODULE.PUBLIC
                            || nodeModule === NODE_MODULE.PRIVATE
                            ? `?module=${nodeModule}`
                            : '';
                        navigate(`${buildCollectionPath(databaseKey)}${moduleQuery}`);
                    }
                }

                const entries = [];

                for (const file of files) {
                    const resolvedCreateType = detectPerFile
                        ? detectFileNodeType(file?.type, file?.name)
                        : createTypeKey;
                    const entry = await createNodeForFile({
                        file,
                        createTypeKey: resolvedCreateType,
                        initialFields,
                        module: nodeModule,
                    });
                    if (entry) entries.push(entry);
                }

                const cleanup = () => {
                    entries.forEach((entry) => {
                        if (entry?.objectUrl) URL.revokeObjectURL(entry.objectUrl);
                    });
                };

                const createdNodes = entries.map((entry) => entry?.node).filter(Boolean);
                // Immediate preview for composers that show attachments while uploading.
                // Callers that adopt these URLs must revoke blob: previews themselves.
                const adoptsObjectUrls = typeof onNodesPrepared === 'function';

                /**
                 * handleUploadError
                 *
                 * Handles upload error.
                 *
                 * @param {Error|unknown} error - error.
                 */
                const handleUploadError = (error) => {
                    if (!adoptsObjectUrls) cleanup();
                    notifyBaseLimitError({
                        error,
                        baseKey: activeBaseKey,
                    });
                    toast.error(error?.message || 'Upload failed.');
                };

                notifyNodesPrepared(createdNodes);

                if (typeof onNodesCreated === 'function') {
                    // Wait for Storage upload before onNodesCreated so success toasts
                    // never fire when the bytes were rejected.
                    await new Promise((resolve, reject) => {
                        uploadService.queueNodeUploads(
                            entries,
                            user,
                            (result) => {
                                applyUploadPatchesToNodes(createdNodes, result);
                                if (!adoptsObjectUrls) cleanup();
                                resolve(result);
                            },
                            (error) => {
                                handleUploadError(error);
                                reject(error);
                            },
                        );
                    });
                    notifyNodesCreated(createdNodes);
                } else {
                    uploadService.queueNodeUploads(
                        entries,
                        user,
                        adoptsObjectUrls ? undefined : cleanup,
                        handleUploadError,
                    );
                }
                return createdNodes;
            }

            const [file] = files;
            const resolvedCreateType = detectPerFile
                ? detectFileNodeType(file?.type, file?.name)
                : createTypeKey;
            const entry = await createNodeForFile({
                file,
                createTypeKey: resolvedCreateType,
                initialFields,
                module: nodeModule,
            });
            const newNode = entry?.node;

            if (shouldNavigateNow) {
                setSelectedLocation && setSelectedLocation(null);
                openNode(newNode);
            }

            const entries = entry ? [entry] : [];
            const cleanup = () => {
                entries.forEach((row) => {
                    if (row?.objectUrl) URL.revokeObjectURL(row.objectUrl);
                });
            };

            const adoptsObjectUrls = typeof onNodesPrepared === 'function';

            /**
             * handleUploadError
             *
             * Handles upload error.
             *
             * @param {Error|unknown} error - error.
             */
            const handleUploadError = (error) => {
                if (!adoptsObjectUrls) cleanup();
                notifyBaseLimitError({
                    error,
                    baseKey: activeBaseKey,
                });
                toast.error(error?.message || 'Upload failed.');
            };

            notifyNodesPrepared(newNode ? [newNode] : []);

            uploadService.queueNodeUploads(
                entries,
                user,
                (result) => {
                    applyUploadPatchesToNodes(newNode ? [newNode] : [], result);
                    if (!adoptsObjectUrls) cleanup();
                    if (adoptsObjectUrls) {
                        notifyNodesCreated(newNode ? [newNode] : []);
                    }
                },
                handleUploadError,
            );
            if (!adoptsObjectUrls) {
                notifyNodesCreated(newNode ? [newNode] : []);
            }
            return newNode || null;
        } catch (error) {
            console.error('Failed to create upload node:', error);
            notifyBaseLimitError({
                error,
                baseKey: activeBaseKey,
            });
            toast.error(error?.message || 'Could not prepare upload.');
            return null;
        }
    }, [
        activeBaseKey,
        assertFilesAllowedForBase,
        createNodeForFile,
        getType,
        language,
        navigate,
        openNode,
        setActiveDatabase,
        setCurrentNode,
        setCurrentSection,
        setSelectedLocation,
        notifyNodesCreated,
        notifyNodesPrepared,
        onNodesCreated,
        onNodesPrepared,
        user,
    ]);

    const handleTopLevelActionSelect = useCallback(async (typeKey, options = {}) => {
        if (!user || !typeKey) return null;

        const initialFields = options?.initialFields || null;
        const relatedNode = options?.relatedNode || null;
        const titleOverride = typeof options?.title === 'string' ? options.title.trim() : '';
        const shouldNavigateOverride = typeof options?.shouldNavigate === 'boolean'
            ? options.shouldNavigate
            : null;
        const shouldNavigateNow = shouldNavigateOverride ?? shouldNavigate;
        const provisional = options?.provisional === true;
        const nodeModule = normalizeNodeModule(options?.module)
            || resolveCreateNodeModule({ consume: true });

        if (typeKey === 'CAMERA' || typeKey === 'SNAPSHOT' || typeKey === 'RECORDING') {
            // CAMERA / SNAPSHOT default to photo; RECORDING opens in video mode.
            // The camera UI can still switch modes before capture.
            const initialMode = typeKey === 'RECORDING' ? 'RECORDING' : 'SNAPSHOT';
            setCameraContentType(initialMode);
            setCameraInitialFields(initialFields);
            setCameraShouldNavigate(shouldNavigateNow);
            setCameraModule(nodeModule);
            setCameraOpen(true);
            return null;
        }

        if (typeKey === MIXED_UPLOAD_TYPE_KEY) {
            setPendingUploadType(MIXED_UPLOAD_TYPE_KEY);
            setPendingUploadCreateType(null);
            setPendingUploadDetectPerFile(true);
            setPendingUploadAccept(MIXED_UPLOAD_ACCEPT);
            setPendingUploadFields(initialFields);
            setPendingUploadNavigate(shouldNavigateNow);
            setPendingUploadMultiple(true);
            setPendingUploadModule(nodeModule);
            requestAnimationFrame(() => uploadInputRef.current?.click());
            return null;
        }

        const def = getType?.(typeKey);
        const resolvedTypeKey = def?.key || typeKey;
        const primitiveKeyUpper = String(resolvedTypeKey).toUpperCase();
        const acceptOverride = typeof def?.uploadAccept === 'string' ? def.uploadAccept.trim() : '';
        const fallbackAccept = DEFAULT_UPLOAD_ACCEPT[primitiveKeyUpper] || '';
        const isUploadType = Boolean(acceptOverride || fallbackAccept || def?.multiUpload);
        // Always include .pdf + Chrome PDF MIME so Finder "Chrome PDF Document" files
        // remain selectable even when Firestore uploadAccept is MIME-only.
        const resolvedAccept = (() => {
            const base = acceptOverride || fallbackAccept || DEFAULT_UPLOAD_ACCEPT.FILE;
            if (primitiveKeyUpper !== 'DOCUMENT' && primitiveKeyUpper !== 'FILE') return base;
            const tokens = new Set(
                String(base)
                    .split(',')
                    .map((token) => token.trim())
                    .filter(Boolean)
            );
            tokens.add('.pdf');
            tokens.add('application/pdf');
            tokens.add('application/x-google-chrome-pdf');
            return Array.from(tokens).join(',');
        })();
        const isMultiUpload = def?.multiUpload === true || ALWAYS_MULTI_UPLOAD_PRIMITIVES.has(primitiveKeyUpper);
        const uploadCreatesType = typeof def?.uploadCreatesType === 'string'
            ? def.uploadCreatesType.trim()
            : '';

        if (isUploadType) {
            setPendingUploadType(typeKey);
            setPendingUploadCreateType(isMultiUpload ? (uploadCreatesType || typeKey) : null);
            setPendingUploadDetectPerFile(false);
            setPendingUploadAccept(resolvedAccept);
            setPendingUploadFields(initialFields);
            setPendingUploadNavigate(shouldNavigateNow);
            setPendingUploadMultiple(isMultiUpload);
            setPendingUploadModule(nodeModule);
            requestAnimationFrame(() => uploadInputRef.current?.click());
            return null;
        }

        try {
            const newNode = await createNodeFromType(
                typeKey,
                user,
                activeBaseKey || null,
                { module: nodeModule },
            );

            const typeDef = getType?.(newNode.type || typeKey);
            const singularLabel = getSingularLabel(typeDef, newNode.type || typeKey, language);
            newNode.title = titleOverride || `New ${singularLabel}`;
            newNode.parentTitle = user.name || 'Profile';
            newNode.parentType = user.type || 'USER';
            newNode.profileRoot = true;

            applyInitialFields(newNode, newNode.type || typeKey, initialFields);

            if (provisional) {
                newNode.provisional = true;
            }

            await createNodeDoc(newNode.key, newNode);

            if (provisional) {
                registerProvisionalSession(newNode);
            }

            if (relatedNode?.key) {
                try {
                    await upsertEdge(
                        relatedNode.key,
                        newNode.key,
                        buildHierarchyEdgeOptions({
                            parentNode: relatedNode,
                            childNode: newNode,
                            baseKey: activeBaseKey || null,
                        }),
                    );
                } catch (error) {
                    console.error('Failed to link created node to related node:', error);
                }
            }

            if (shouldNavigateNow) {
                setSelectedLocation && setSelectedLocation(null);
                openNode(newNode);
            }

            notifyNodesCreated([newNode]);

            return newNode;
        } catch (error) {
            console.error('Failed to create node:', error);
            toast.error(error?.message || 'Could not create node.');
            return null;
        }
    }, [
        user,
        activeBaseKey,
        getType,
        language,
        openNode,
        setSelectedLocation,
        applyInitialFields,
        notifyNodesCreated,
        shouldNavigate,
        setCameraContentType,
        setCameraInitialFields,
        setCameraShouldNavigate,
        setCameraOpen,
    ]);

    const handleUploadFileSelect = useCallback(async (event) => {
        const files = Array.from(event?.target?.files || []);
        if (event?.target) event.target.value = '';

        const typeKey = pendingUploadType;
        const detectPerFile = pendingUploadDetectPerFile;
        const createTypeKey = detectPerFile
            ? null
            : (pendingUploadCreateType || typeKey);
        const initialFields = pendingUploadFields;
        const isMultiUpload = pendingUploadMultiple;
        const nodeModule = pendingUploadModule;
        setPendingUploadType(null);
        setPendingUploadCreateType(null);
        setPendingUploadAccept('');
        setPendingUploadFields(null);
        setPendingUploadMultiple(false);
        setPendingUploadDetectPerFile(false);
        setPendingUploadModule(NODE_MODULE.PRIVATE);
        const shouldNavigateNow = pendingUploadNavigate ?? shouldNavigate;
        setPendingUploadNavigate(null);
        await processUploadFiles({
            files,
            typeKey,
            createTypeKey,
            initialFields,
            isMultiUpload,
            shouldNavigateNow,
            detectPerFile,
            module: nodeModule,
        });
    }, [
        pendingUploadType,
        pendingUploadCreateType,
        pendingUploadFields,
        pendingUploadNavigate,
        pendingUploadMultiple,
        pendingUploadDetectPerFile,
        pendingUploadModule,
        processUploadFiles,
        shouldNavigate,
    ]);

    const handleCameraContentCreated = useCallback(async (capturedContent) => {
        const mimeTypeHint = String(capturedContent?.mimeType || capturedContent?.tempFile?.type || capturedContent?.localFile?.type || '').toLowerCase();
        const typeHint = String(capturedContent?.type || '').toUpperCase();
        const typeFromIntent = (
          typeHint === 'VIDEO'
          || mimeTypeHint.startsWith('video/')
          || cameraContentType === 'RECORDING'
        ) ? 'VIDEO' : 'PHOTO';
        const mimeType = capturedContent?.mimeType || (typeFromIntent === 'VIDEO' ? 'video/webm' : 'image/jpeg');
        const fallbackName = capturedContent?.fileName
            || `${typeFromIntent.toLowerCase()}-${Date.now()}${typeFromIntent === 'VIDEO' ? '.webm' : '.jpg'}`;

        let file = capturedContent?.tempFile || capturedContent?.localFile || null;
        if (file instanceof Blob && !(file instanceof File)) {
            file = new File([file], fallbackName, { type: file.type || mimeType });
        }

        setCameraOpen(false);
        setCameraContentType(null);

        const initialFields = cameraInitialFields;
        const shouldNavigateNow = cameraShouldNavigate ?? shouldNavigate;
        const nodeModule = cameraModule;
        setCameraInitialFields(null);
        setCameraShouldNavigate(null);
        setCameraModule(NODE_MODULE.PRIVATE);

        if (!file) {
            console.warn('Camera capture did not produce an uploadable file.');
            return;
        }

        await processUploadFiles({
            files: [file],
            typeKey: typeFromIntent,
            createTypeKey: typeFromIntent,
            initialFields,
            isMultiUpload: false,
            shouldNavigateNow,
            module: nodeModule,
        });
    }, [
        cameraContentType,
        cameraInitialFields,
        cameraModule,
        cameraShouldNavigate,
        processUploadFiles,
        shouldNavigate,
    ]);

    return {
        cameraOpen,
        cameraContentType,
        uploadInputRef,
        pendingUploadAccept,
        pendingUploadMultiple,
        handleTopLevelActionSelect,
        handleUploadFileSelect,
        setCameraOpen,
        handleCameraContentCreated,
        /**
         * Create Library nodes from OS files (mixed PHOTO/VIDEO/DOCUMENT/FILE) without navigating.
         * Used by desktop drop / browse-from-disk flows via onNodesCreated.
         */
        ingestMixedUploadFiles: async (files, options = {}) => {
            const list = Array.from(files || []).filter(Boolean);
            if (!list.length) return [];
            return processUploadFiles({
                files: list,
                typeKey: MIXED_UPLOAD_TYPE_KEY,
                createTypeKey: null,
                initialFields: options.initialFields || null,
                isMultiUpload: true,
                shouldNavigateNow: options.shouldNavigate === true,
                detectPerFile: true,
                module: options.module || NODE_MODULE.PRIVATE,
            });
        },
    };
}
